export const ASCENDING = 'ascending' as const;
export const DESCENDING = 'descending' as const;

export type SortDirection = typeof ASCENDING | typeof DESCENDING | undefined;

export type Column<TData> = {
  key: keyof TData & string;
  header: string;
};

export type Header = {
  id: string;
  label: string;
  sort: {
    active: boolean;
    direction: SortDirection;
    /** Cycle: unsorted → ascending → descending → unsorted. */
    toggle: () => void;
    /** Set the column's sort direction explicitly. `undefined` clears the sort. */
    set: (direction: SortDirection) => void;
  };
};

export type Filter = {
  value: string;
  set: (value: string) => void;
};

export type Pagination = {
  pageNumber: number;
  pageSize: number;
  pageCount: number;
  /** Total rows in the filtered set (across all pages). */
  totalRows: number;
  /** 1-based index of the first visible row, or 0 when empty. */
  rangeStart: number;
  /** 1-based index of the last visible row, or 0 when empty. */
  rangeEnd: number;
  canPrevious: boolean;
  canNext: boolean;
  previous: () => void;
  next: () => void;
  goTo: (pageNumber: number) => void;
};

export type ClientTableOptions<TData> = {
  data: TData[];
  columns: Column<TData>[];
  initialPageSize?: number;
  initialPageNumber?: number;
  initialFilter?: string;
  initialSortKey?: keyof TData & string;
  initialSortDirection?: typeof ASCENDING | typeof DESCENDING;
  /** Fires whenever filter, sort, or pagination state changes (and on mount). */
  onChange?: (state: ClientTableState<TData>) => void;
};

export type ClientTableResult<TData> = {
  rows: TData[];
  headers: Header[];
  filter: Filter;
  pagination: Pagination;
};

/** Flat, URL-friendly snapshot of the hook's controllable state. */
export type ClientTableState<TData> = {
  filter: string;
  pageNumber: number;
  pageSize: number;
  sortKey: (keyof TData & string) | null;
  sortDirection: SortDirection;
};
